import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveImageUrl } from '../lib/api';
import { ArrowRight, Mic, Zap } from 'lucide-react';

export default function FeaturedInterviewCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/interviews/featured').then((res) => {
      if (!cancelled && res.data) setData(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const hero = resolveImageUrl(data.hero_image) || resolveImageUrl((data.gallery_images || [])[0]);
  const link = `/interviews/${data.slug || data.id}`;

  return (
    <Link
      to={link}
      className="group block relative overflow-hidden rounded-xl border-2 border-[#262626] hover:border-[#FF3B30] transition-all duration-300 shadow-2xl"
      data-testid="featured-interview-card"
      style={{
        // skewed racing-style corner cut using clip-path
        clipPath: 'polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)',
      }}
    >
      <div className="relative aspect-[4/5] md:aspect-[3/4] bg-[#0A0A0A] overflow-hidden">
        {hero && (
          <img
            src={hero}
            alt={`${data.driver_name} featured interview`}
            className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
            loading="eager"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
        <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-transparent via-[#FF3B30] to-transparent" />

        {/* Ribbon */}
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#FF3B30] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-sm shadow-lg">
          <Zap className="w-3 h-3" />
          Featured Interview
        </div>
        {data.sponsor_name && (
          <div className="absolute top-3 right-3 bg-black/70 border border-[#262626] text-gray-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
            Powered by {data.sponsor_name}
          </div>
        )}

        {/* Content */}
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Mic className="w-4 h-4 text-[#FF3B30]" />
            <span className="text-gray-400 text-[10px] uppercase tracking-[0.25em] font-bold">
              Driver Spotlight
            </span>
          </div>
          <h2 className="font-heading font-black text-2xl md:text-3xl text-white leading-tight mb-2" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
            {data.driver_name}
          </h2>
          {data.team_name && (
            <p className="text-[#FF3B30] font-bold text-sm uppercase tracking-wider mb-3">
              {data.team_name}
            </p>
          )}
          {data.subtitle && (
            <p className="text-gray-200 text-sm md:text-base leading-snug mb-4 line-clamp-3" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              {data.subtitle}
            </p>
          )}
          <div className="inline-flex items-center gap-2 bg-[#FF3B30] group-hover:bg-white group-hover:text-[#FF3B30] text-white text-xs md:text-sm font-black uppercase tracking-widest px-4 py-2.5 rounded transition-colors">
            Read Full Interview
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>

        {/* Diagonal accent line */}
        <div className="absolute bottom-0 right-0 w-24 h-24 pointer-events-none">
          <div className="absolute bottom-0 right-0 w-32 h-1 bg-[#FF3B30] origin-right rotate-[-45deg] translate-x-4" />
        </div>
      </div>
    </Link>
  );
}
